export const reactQuestions = [
  {
    id: 1,
    question: "What is JSX?",
    parts: [
      {
        type: "text",
        content: `**JSX (JavaScript XML)** is a syntax extension for JavaScript that looks similar to HTML.

**Key Features**
- 📝 Write HTML-like markup directly inside JavaScript
- 🔧 Transpiles into \`React.createElement()\` calls (via Babel)
- ⚠️ Not valid JavaScript without compilation
- 🧠 Supports JavaScript expressions inside \`{ }\`

**Benefits**
- 📘 Easier to read and understand
- 👨‍💻 Familiar syntax for web developers
- 🔒 Helps prevent XSS attacks (auto-escapes values)
- 🧪 Enables static analysis and better error messages
`
      },
      {
        type: "code",
        language: "jsx",
        content: "const element = <h1>Hello, {name}!</h1>;\n// Transpiles to:\nReact.createElement('h1', null, 'Hello ', name, '!');"
      }
    ]
  },
  {
    id: 2,
    question: "What's the difference between SPA and Server-Side Rendering in React?",
    parts: [
      {
        type: "text",
        content: `**SPA vs SSR** - Two different approaches to rendering React applications.

**SPA (Single Page Application)**
- ⚡ React renders on **client-side only**
- 📦 Entire app bundle loads on first request
- 🔄 Subsequent navigation doesn't reload page
- 🚀 Fast after initial load, but slower first paint
- 💡 Best for: Internal tools, dashboards, apps behind login

**SSR (Server-Side Rendering)**
- 🏗️ React renders on **server**, sends HTML to client
- ⚡ React then "hydrates" the HTML on client-side
- 🔍 Better **SEO** and initial load performance
- 📱 Improved performance on slower devices
- 🛠️ **Frameworks**: Next.js, Remix, Gatsby

**📍 Where React Renders**
React mounts to a root element in HTML, typically a \`div\` with \`id="root"\`. React takes over this element and manages everything inside it.`
      },
      {
        type: "code",
        language: "jsx",
        content: "<!-- HTML -->\n<div id=\"root\"></div>\n\n// JavaScript\nReactDOM.createRoot(document.getElementById('root')).render(<App />);"
      }
    ]
  },
  {
    id: 3,
    question: "When would you use Redux vs Context API? What are the differences?",
    parts: [
      {
        type: "text",
        content: `
**Context API**
- ✅ Built into React
- 👍 Good for simple/moderate global state (theme, auth, language)
- 🔄 All consumers re-render when value changes
- ⚙️ No middleware or time-travel debugging
- 🧩 Simple setup

**Redux**
- 📦 External library for complex state management
- 🔐 Predictable state container
- 🔌 Powerful middleware (thunks, sagas)
- 🕰️ Time-travel debugging with DevTools
- 📍 Single source of truth
- 📝 More boilerplate but structured

**When to Use**
- Context: Small apps, simple shared state, up to a few contexts
- Redux: Large apps, complex logic, need debugging tools, async workflows`
      },
      {
        type: "code",
        language: "jsx",
        content: "// Context API\nconst ThemeContext = createContext();\n<ThemeContext.Provider value={theme}>\n  {children}\n</ThemeContext.Provider>\n\n// Redux\nconst store = createStore(reducer);\ndispatch({ type: 'UPDATE_USER', payload: user });"
      }
    ]
  },
  {
    id: 4,
    question: "How is lazy loading handled in React?",
    parts: [
      {
        type: "text",
        content: `
React ships with \`React.lazy()\` and \`<Suspense/>\` for code splitting.

**Common Use Cases**
1. Route-based splitting: Load routes on demand
2. Component-based splitting: Defer heavy components
3. Image lazy loading: Use \`loading="lazy"\` on \`<img/>\`
4. Scroll-based loading: IntersectionObserver to fetch on visibility

**Benefits**
- 📦 Smaller initial bundle
- ⚡ Faster first load
- 🧭 Better perceived performance
- 🔄 Load resources only when needed`
      },
      {
        type: "code",
        language: "jsx",
        content: "const LazyComponent = React.lazy(() => import('./LazyComponent'));\n\n<Suspense fallback={<Loading />}>\n  <LazyComponent />\n</Suspense>\n\n// Route example\nconst Home = lazy(() => import('./pages/Home'));"
      }
    ]
  },
  {
    id: 5,
    question: "When a parent component re-renders, what happens to child components?",
    parts: [
      {
        type: "text",
        content: `
By default, when a parent re-renders, its children also re-render.

**Why**
- Keeps UI consistent with state changes
- Simple default model

**Triggers for Re-render**
1. State changes (\`useState\`, \`useReducer\`)
2. Props change
3. Parent re-renders
4. Context value changes
5. Forced updates (rare)

**Preventing Unnecessary Re-renders**
- Wrap child with \`React.memo\` when props are stable
- Memoize callbacks and values passed as props`
      },
      {
        type: "code",
        language: "jsx",
        content: "function Parent() {\n  const [count, setCount] = useState(0);\n  return <Child name=\"John\" />; // Child re-renders with Parent\n}\n\nconst Child = React.memo(({ name }) => <div>{name}</div>);"
      }
    ]
  },
  {
    id: 6,
    question: "How to handle unnecessary re-renders — different techniques?",
    parts: [
      {
        type: "text",
        content: `
**Techniques**
1. \`React.memo\`: Memoize components by props
2. \`useMemo\`: Memoize expensive computations
3. \`useCallback\`: Memoize function references
4. Right state location: Keep state close to usage
5. Composition: Pass children to avoid prop changes
6. Stable \`key\` for lists
7. Code splitting for heavy components

🔎 Profile first (React DevTools), then optimize.`
      },
      {
        type: "code",
        language: "jsx",
        content: "const Child = React.memo(({ data }) => <div>{data}</div>);\nconst expensiveValue = useMemo(() => compute(a, b), [a, b]);\nconst handleClick = useCallback(() => doSomething(id), [id]);"
      }
    ]
  },
  {
    id: 7,
    question: "How is performance handled in React?",
    parts: [
      {
        type: "text",
        content: `**React Performance Strategies**

1. Prevent unnecessary renders: \`React.memo\`, \`useMemo\`, \`useCallback\`
2. Code splitting: \`React.lazy\` + \`<Suspense/>\`
3. List virtualization: \`react-window\`, \`react-virtualized\`
4. Debounce/Throttle expensive handlers
5. Lazy load images/components/routes
6. Use production builds (minification, tree-shaking)
7. Proper keys for lists
8. Avoid inline functions/objects; memoize where helpful
9. Profile with DevTools (React Profiler)
10. Automatic batching in React 18 reduces re-renders`
      }
    ]
  },
  {
    id: 8,
    question: "List several of the most used hooks in React",
    parts: [
      {
        type: "text",
        content: `**Most-used React Hooks**

1. \`useState\`: Local component state
2. \`useEffect\`: Side effects (fetching, subscriptions)
3. \`useContext\`: Read context values
4. \`useRef\`: DOM refs and persistent mutable values
5. \`useMemo\`: Memoize expensive computations
6. \`useCallback\`: Memoize functions
7. \`useReducer\`: Complex state logic patterns
8. \`useLayoutEffect\`: Synchronous effects before paint
9. Custom hooks: Extract reusable logic`
      },
      {
        type: "code",
        language: "jsx",
        content: "const [count, setCount] = useState(0);\nuseEffect(() => { fetchData(); }, []);\nconst theme = useContext(ThemeContext);\nconst inputRef = useRef(null);\nconst value = useMemo(() => compute(), [deps]);\nconst handler = useCallback(() => {}, [deps]);\nconst [state, dispatch] = useReducer(reducer, initial);\nuseLayoutEffect(() => { measure(); }, []);"
      }
    ]
  },
  {
    id: 9,
    question: "What is the purpose of useEffect, when it's used, and when it shouldn't be used?",
    parts: [
      {
        type: "text",
        content: `
Handle side effects after render.

**Use It For**
1. Data fetching
2. Subscriptions
3. DOM manipulation
4. Timers
5. Event listeners

**Avoid Using For**
1. Pure data transforms (use variables/\`useMemo\`)
2. User event handling (use handlers)
3. Initializing state (state initializer)
4. Resetting via remount (\`key\` prop)

**Dependency Array**
- \`[]\`: run once on mount
- \`[deps]\`: run when dependencies change
- no array: runs every render (usually incorrect)`
      },
      {
        type: "code",
        language: "jsx",
        content: "useEffect(() => {\n  fetchData();\n  return () => cleanup();\n}, [dependencies]);"
      }
    ]
  },
  {
    id: 10,
    question: "What is the difference between useCallback and useMemo?",
    parts: [
      {
        type: "text",
        content: `
**useCallback**
- Memoizes FUNCTION references
- Returns the function itself
- Prevents new function creation per render
- Use when passing callbacks to memoized children

**useMemo**
- Memoizes COMPUTED VALUES
- Returns the computed result
- Avoids expensive recalculations
- Use for heavy computations

🔑 \`useCallback(fn, deps)\` ≈ \`useMemo(() => fn, deps)\``
      },
      {
        type: "code",
        language: "jsx",
        content: "// useCallback\nconst handleClick = useCallback(() => {\n  doSomething(id);\n}, [id]);\n\n// useMemo\nconst expensiveValue = useMemo(() => {\n  return computeExpensiveValue(a, b);\n}, [a, b]);"
      }
    ]
  },
  {
    id: 11,
    question: "What is memoization?",
    parts: [
      {
        type: "text",
        content: `
Caching results of expensive computations to avoid rework.

**How It Works**
1. Call function with arguments
2. Cache the result
3. Same args → return cached result
4. Skip recalculation

**In React**
- \`React.memo\`: Skip re-render if props unchanged
- \`useMemo\`: Cache computed values
- \`useCallback\`: Cache function references

✅ Improves performance; ⚠️ trades memory for speed.`
      },
      {
        type: "code",
        language: "jsx",
        content: "const expensiveValue = useMemo(() => {\n  return computeExpensiveValue(a, b);\n}, [a, b]);"
      }
    ]
  },
  {
    id: 12,
    question: "What is local state vs global state?",
    parts: [
      {
        type: "text",
        content: `

**Local State**
- Used by a single component (and children)
- Managed with \`useState\` or \`useReducer\`
- Prefer when possible (simpler, isolated)

Examples: form inputs, toggles, local UI states

**Global State**
- Shared across multiple components
- Managed with Context, Redux, Zustand, etc.
- Use when prop drilling becomes problematic

Examples: auth, theme, cart, locale

**Best Practices**
1. Start local
2. Lift state up for shared needs
3. Move to global only when necessary
4. Keep global minimal
5. Split by domain (multiple contexts/stores)`
      },
      {
        type: "code",
        language: "jsx",
        content: "const [isOpen, setIsOpen] = useState(false);"
      }
    ]
  },
  {
    id: 13,
    question: "When would you use useRef and why?",
    parts: [
      {
        type: "text",
        content: `
Creates a mutable object that persists across renders without causing re-renders.

**Use Cases**
1. Access DOM elements
2. Store mutable values
3. Track previous values
4. Manage timers/intervals

**Why**
- Updates do not trigger re-renders
- Persists across renders
- Direct DOM access when necessary

ℹ️ Changing \`.current\` does not re-render.`
      },
      {
        type: "code",
        language: "jsx",
        content: "// 1. DOM refs\nconst inputRef = useRef(null);\n<input ref={inputRef} />\ninputRef.current.focus();\n\n// 2. Mutable values\nconst countRef = useRef(0);\ncountRef.current++;\n\n// 3. Previous value\nconst prevValue = useRef();\nuseEffect(() => {\n  prevValue.current = value;\n}, [value]);\n\n// 4. Timers\nconst timerRef = useRef();\ntimerRef.current = setInterval(() => {}, 1000);\nclearInterval(timerRef.current);"
      }
    ]
  },
  {
    id: 14,
    question: "What is the point of the return function in useEffect?",
    parts: [
      {
        type: "text",
        content: `
The return function from \`useEffect\` is for cleanup.

**Purpose**
- Prevent memory leaks
- Run before unmount
- Run before the effect re-runs on dependency change

**When It Runs**
- On unmount
- Before next effect run (if deps changed)
- Not on initial mount`
      },
      {
        type: "code",
        language: "jsx",
        content: "// Clear timers\nuseEffect(() => {\n  const timer = setTimeout(() => {}, 1000);\n  return () => clearTimeout(timer);\n}, []);\n\n// Unsubscribe\nuseEffect(() => {\n  const subscription = subscribe();\n  return () => subscription.unsubscribe();\n}, []);\n\n// Remove event listeners\nuseEffect(() => {\n  window.addEventListener('resize', handleResize);\n  return () => window.removeEventListener('resize', handleResize);\n}, []);\n\n// Cancel API requests\nuseEffect(() => {\n  const controller = new AbortController();\n  fetch(url, { signal: controller.signal });\n  return () => controller.abort();\n}, [url]);"
      }
    ]
  },
  {
    id: 15,
    question: "What are the different usages of the dependency array in useEffect?",
    parts: [
      {
        type: "text",
        content: `**useEffect Dependency Patterns**

1. \`[]\`: run on mount (one-time setup, initial fetch)
2. \`[deps]\`: run when dependencies change (most common)
3. No array: run after EVERY render (rarely correct)

**Best Practices**
- Include all used values from component scope
- Use eslint-plugin-react-hooks
- Missing deps → stale closures/bugs
- Too many deps → refactor effect or logic

ℹ️ React 18 Strict Mode runs effects twice in dev to catch issues.`
      },
      {
        type: "code",
        language: "jsx",
        content: "// 1. Empty array []\nuseEffect(() => {\n  fetchData();\n}, []);\n\n// 2. With dependencies [userId]\nuseEffect(() => {\n  fetchUser(userId);\n}, [userId]);\n\n// 3. No dependency array\nuseEffect(() => {\n  console.log('Rendered');\n});"
      }
    ]
  },
  {
    id: 16,
    question: "What is controlled vs uncontrolled input?",
    parts: [
      {
        type: "text",
        content: `
**Controlled**
- Value managed by React state (source of truth)
- Easy validation and full control

**Uncontrolled**
- Value managed by the DOM
- Access via refs when needed
- Simpler for basic forms; required for file inputs

✅ Prefer controlled by default; use uncontrolled for simple/file inputs.`
      },
      {
        type: "code",
        language: "jsx",
        content: "// Controlled\n<input value={name} onChange={(e) => setName(e.target.value)} />\n\n// Uncontrolled\n<input ref={inputRef} defaultValue=\"initial\" />\nconst value = inputRef.current.value;"
      }
    ]
  },
  {
    id: 17,
    question: "Why are keys important in React lists?",
    parts: [
      {
        type: "text",
        content: `
Keys help React identify which items changed, were added, or removed.

**Purpose**
- Optimize reconciliation performance
- Maintain correct component state

**Good Keys**
- Unique, stable IDs (e.g., \`item.id\`)

**Bad Keys**
- Array index (if list can reorder)
- Random values (\`Math.random()\`)
- Non-unique keys

⚠️ Wrong keys can cause rendering/state bugs on reorder.`
      },
      {
        type: "code",
        language: "jsx",
        content: "{items.map(item => (\n  <TodoItem key={item.id} item={item} />\n))}"
      }
    ]
  },
  {
    id: 18,
    question: "What are the benefits of Server-Side Rendering (SSR)?",
    parts: [
      {
        type: "text",
        content: `

**Pros**
1. Better SEO (full HTML for crawlers, social previews)
2. Faster FCP (content appears sooner)
3. Better on slow devices (less client work)
4. Progressive enhancement (works with limited JS)

**Cons**
- More server load and complexity
- Higher hosting costs
- Potentially longer TTI

**Use When**
- Public content, e-commerce, marketing, SEO-critical pages

**Avoid When**
- Authenticated dashboards, highly interactive apps

Frameworks: Next.js, Remix, Gatsby (SSG). Hybrid: SSR for public, CSR for authed.`
      }
    ]
  },
  {
    id: 19,
    question: "useEffect vs useLayoutEffect — when to use each?",
    parts: [
      {
        type: "text",
        content: `**useEffect vs useLayoutEffect**

**useEffect**
- Runs AFTER paint (async)
- Doesn't block visual updates
- Use for 99% of side effects

**useLayoutEffect**
- Runs BEFORE paint (sync)
- Blocks paint until complete
- Use for DOM measurements, flicker prevention, scroll/focus, animation prep

Flow:
- useEffect: Render → Paint → Effect
- useLayoutEffect: Render → Effect → Paint

⚠️ Use \`useLayoutEffect\` sparingly to avoid jank.`
      },
      {
        type: "code",
        language: "jsx",
        content: "useLayoutEffect(() => {\n  const height = divRef.current.offsetHeight;\n  setHeight(height);\n}, []);"
      }
    ]
  },
  {
    id: 20,
    question: "How do you test React components?",
    parts: [
      {
        type: "text",
        content: `
Use React Testing Library (RTL) + Jest.

**Principles**
- Test user-visible behavior (not implementation)
- Query by accessibility (roles, labels, text)
- Simulate interactions and assert outcomes

**Async**
- Use \`waitFor\` or \`findBy*\` queries
- Mock APIs with \`jest.mock()\`

Run tests: \`npm test\``
      },
      {
        type: "code",
        language: "jsx",
        content: "import { render, screen } from '@testing-library/react';\n\ntest('button shows success message on click', async () => {\n  render(<MyComponent />);\n  const button = screen.getByRole('button');\n  await userEvent.click(button);\n  expect(screen.getByText(/success/i)).toBeInTheDocument();\n});"
      }
    ]
  },
  {
    id: 21,
    question: "What is the difference between unit and integration tests?",
    parts: [
      {
        type: "text",
        content: `
**Unit**
- Test pieces in isolation; mock dependencies
- Fast, focused, many of them

**Integration**
- Test modules/components working together
- Slower, broader, fewer

**Pyramid**
- Many unit → fewer integration → few E2E

In React:
- Unit: component with mocks
- Integration: multiple components + real context/routing
- E2E: full app (Cypress, Playwright)

RTL philosophy: favor integration tests for user behavior.`
      }
    ]
  },
  {
    id: 22,
    question: "What is automatic batching in React 18?",
    parts: [
      {
        type: "text",
        content: `
Groups multiple state updates into a single re-render across async boundaries.

**Before (React 17)**
- Batched only in React event handlers
- Not batched in promises, \`setTimeout\`, native events

**Now (React 18)**
- Batches updates across promises, timers, native events
- Fewer re-renders, better performance

Works with \`useState\`, \`useReducer\`, Context. Opt-out via \`flushSync\`.`
      },
      {
        type: "code",
        language: "jsx",
        content: "// React 18 - Single re-render\nsetTimeout(() => {\n  setCount(c => c + 1);\n  setFlag(f => !f);\n}, 1000);\n\n// Opt-out\nimport { flushSync } from 'react-dom';\nflushSync(() => {\n  setCount(c => c + 1);\n});"
      }
    ]
  },
  {
    id: 23,
    question: "What are React Portals?",
    parts: [
      {
        type: "text",
        content: `
Render children into a DOM node outside the parent hierarchy.

Syntax: \`ReactDOM.createPortal(child, container)\`.

**Use Cases**
- Modals, tooltips, dropdowns, toasts

**Why**
- Escape parent \`overflow: hidden\` and z-index contexts
- Position globally; avoid CSS conflicts

Notes: Events bubble through React tree; context works normally.`
      },
      {
        type: "code",
        language: "jsx",
        content: "function Modal({ children }) {\n  return ReactDOM.createPortal(\n    <div className=\"modal\">{children}</div>,\n    document.getElementById('modal-root')\n  );\n}"
      }
    ]
  },
  {
    id: 24,
    question: "How do you lift state up in React?",
    parts: [
      {
        type: "text",
        content: `
Move state to the closest common ancestor of components that share it.

**When**
1. Multiple components need same data
2. Components must stay in sync
3. Child needs to update parent state

**Alternatives**
- Context to avoid prop drilling
- State libraries (Redux, Zustand)

Principle: Keep state at the lowest common ancestor that needs it.`
      },
      {
        type: "code",
        language: "jsx",
        content: "function Parent() {\n  const [data, setData] = useState('');\n\n  return (\n    <>\n      <ComponentA data={data} setData={setData} />\n      <ComponentB data={data} />\n    </>\n  );\n}\n\nfunction ComponentA({ data, setData }) {\n  return <input value={data} onChange={e => setData(e.target.value)} />;\n}\n\nfunction ComponentB({ data }) {\n  return <div>{data}</div>;\n}"
      }
    ]
  },
  {
    id: 25,
    question: "What is the spread operator (...) used for?",
    parts: [
      {
        type: "text",
        content: `
Expands arrays/objects; creates shallow copies.

**Common Uses**
1. Copy arrays/objects
2. Merge arrays/objects
3. Add items immutably
4. Pass props in React
5. Spread function arguments

Note: Shallow copy only; mind nested structures.`
      },
      {
        type: "code",
        language: "jsx",
        content: "// 1. Copy\nconst copyArr = [...originalArray];\nconst copyObj = { ...originalObject };\n\n// 2. Merge\nconst combined = [...arr1, ...arr2];\nconst merged = { ...obj1, ...obj2 };\n\n// 3. Add items\nconst newArr = [...items, newItem];\nconst updated = { ...user, age: 30 };\n\n// 4. Pass props\n<Component {...props} />\n\n// 5. Function args\nMath.max(...numbers);"
      }
    ]
  },
  {
    id: 26,
    question: "Give an example of a custom hook",
    parts: [
      {
        type: "text",
        content: `
Extract and reuse stateful logic across components.

**Why**
- Share logic, keep components focused
- Better organization and testability
- DRY principle

Common hooks: \`useLocalStorage\`, \`useDebounce\`, \`useWindowSize\`, \`useToggle\`.

Rules: Start with \`use\`, can call other hooks, follow hook rules.`
      },
      {
        type: "code",
        language: "jsx",
        content: "function useFetch(url) {\n  const [data, setData] = useState(null);\n  const [loading, setLoading] = useState(true);\n\n  useEffect(() => {\n    fetch(url)\n      .then(res => res.json())\n      .then(setData)\n      .finally(() => setLoading(false));\n  }, [url]);\n\n  return { data, loading };\n}\n\n// Usage\nfunction UserProfile() {\n  const { data, loading } = useFetch('/api/user');\n  if (loading) return <Loading />;\n  return <div>{data.name}</div>;\n}"
      }
    ]
  }
];
